import { HttpEvent, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { iif, Observable, of, throwError } from 'rxjs';
import { catchError, map, mapTo, switchMap, take } from 'rxjs/operators';

import {
    ApiFragment,
    ApiQuery,
    ApiQueryBuilder,
    ApiRecordType,
    CustomHttpParamEncoder,
    FacialRecognitionConsent,
    Family,
    FragmentId,
    Identifier,
    makeSequentialRequests,
    MediaObjectId,
    MergeStrategy,
    Metadata,
    PartialMetadata,
    PartialRecord,
    QueryPart,
    QueryPartStrategy,
    RecordId,
    RecordStatus,
    RecordTypeStructure,
    Restriction,
    ValidationResponse,
    ValidationResponseType,
    ZoneId
} from '@stylehaven/components';
import { IngestSpaceId } from '../../entities/mediahaven-api/ingestspace.entity';
import { waitForIndex } from './helpers/wait-for-index.helper';
import { WaitForRecordDelete, WaitForRecordUpdate } from './helpers/wait-for-record-update.helper';
import { SearchService } from './search.service';

export type CreateRecordOptions = {
    parentRecordId?: MediaObjectId;
} & ({ zoneId: ZoneId } | { ingestSpaceId?: IngestSpaceId }) & { publish?: boolean };

@Injectable({ providedIn: 'root' })
export class RecordsService extends SearchService {
    private appendMetadataAsJson(fd: FormData, record: PartialRecord): FormData {
        const metadataBlob: Blob = new Blob([record.asJsonString()], { type: 'application/json' });
        fd.append('metadata', metadataBlob);
        return fd;
    }

    public addRecordToParent(record: ApiFragment, parent: ApiFragment): Observable<ApiFragment> {
        const parentRecordId: MediaObjectId = parent.getMediaObjectId();
        const request = `${this.recordsEndpoint}/${record.getFragmentId()}`;
        const fd: FormData = this.appendMetadataAsJson(new FormData(), new PartialRecord());
        fd.append('adoption.parentRecordId', parentRecordId);
        fd.append('adoption.profileStrategy', 'OVERWRITE');

        const saveStream$: Observable<ApiFragment> = this.http
            .post<Metadata>(request, fd)
            .pipe(map(record => new ApiFragment(record)));

        return saveStream$.pipe(
            switchMap(updatedRecord =>
                WaitForRecordUpdate(record.getFragmentId(), this, updatedRecord.getLastModifiedDate())
            )
        );
    }

    public addRecordsToParent(records: readonly ApiFragment[], intellectual: ApiFragment): Observable<ApiFragment[]> {
        const requests$: Observable<ApiFragment>[] = records.map(record =>
            this.addRecordToParent(record, intellectual)
        );
        return makeSequentialRequests(requests$);
    }

    public removeRecordFromParent(record: ApiFragment, parentId: MediaObjectId): Observable<ApiFragment> {
        const classificationRef: MediaObjectId | null = record.getClassificationId();

        if (!classificationRef) {
            return throwError(
                new Error('Remove from parent: ClassificationReferenceCode of record should not be undefined')
            );
        }

        const parentRef: MediaObjectId | undefined = record.getDirectParentRefCode();
        const candidateParentRef: FragmentId | null = record.getCandidateParentId();

        if (parentRef !== parentId && !record.isCandidateChild()) {
            return throwError(
                new Error(`Can't remove from parent '${parentId}' because parent ReferenceCode is '${parentRef}'.`)
            );
        }

        const request = `${this.recordsEndpoint}/${record.getFragmentId()}`;
        let fd: FormData;

        // TODO: Remove in 26.4
        if (candidateParentRef) {
            const metadata: PartialRecord = new PartialRecord();
            metadata.set(Family.Structural, 'Relations', {
                IsCandidateChildOf: []
            });
            metadata.addMergeStrategy('Relations', MergeStrategy.OVERWRITE);
            fd = this.appendMetadataAsJson(new FormData(), metadata);
        } else {
            fd = this.appendMetadataAsJson(new FormData(), new PartialRecord());
            fd.append('adoption.parentRecordId', classificationRef);
            fd.append('adoption.candidate', 'false');
            fd.append('adoption.profileStrategy', 'OVERWRITE');
        }

        const saveStream$: Observable<ApiFragment> = this.http
            .post<Metadata>(request, fd)
            .pipe(map(record => new ApiFragment(record)));

        return saveStream$.pipe(
            switchMap(updatedRecord =>
                WaitForRecordUpdate(record.getFragmentId(), this, updatedRecord.getLastModifiedDate())
            )
        );
    }

    public setAsCover(record: ApiFragment, targetRecord: ApiFragment): Observable<ApiFragment> {
        const recordId: RecordId = record.getRecordId();
        const request = `${this.recordsEndpoint}/${targetRecord.getFragmentId()}`;

        const fd: FormData = this.appendMetadataAsJson(new FormData(), targetRecord);
        fd.append('previewRecordId', recordId);

        const saveStream$: Observable<ApiFragment> = this.http
            .post<Metadata>(request, fd)
            .pipe(map(record => new ApiFragment(record)));
        return saveStream$.pipe(
            switchMap(updatedTarget =>
                WaitForRecordUpdate(targetRecord.getFragmentId(), this, updatedTarget.getLastModifiedDate())
            )
        );
    }

    public uploadFile(
        file: File,
        zoneId: ZoneId,
        recordType: ApiRecordType,
        parentRecordId: MediaObjectId | undefined,
        facialRecognitionConsent?: FacialRecognitionConsent
    ): Observable<HttpEvent<unknown>> {
        const metadata: PartialMetadata = {
            Administrative: {
                RecordStatus: RecordStatus.Processing
            }
        };

        if (facialRecognitionConsent !== undefined) {
            metadata['Governance'] = {
                Consent: {
                    FacialRecognition: facialRecognitionConsent
                }
            };
        }

        const fd: FormData = this.getRecordCreationFormData(zoneId, recordType, metadata as Metadata, parentRecordId);
        fd.append(
            'workflow',
            recordType.Structure === RecordTypeStructure.DataFlat ? 'media_upload' : recordType.Workflow!
        );
        fd.append('file', file);

        return this.http.postProgress(this.recordsEndpoint, fd);
    }

    public uploadRepresentation(file: File, fragmentId: FragmentId): Observable<HttpEvent<unknown>> {
        const endpoint: string = `${this.recordsEndpoint}/${fragmentId}/representations`;
        const fd: FormData = new FormData();

        fd.append('title', file.name);
        fd.append('file', file);
        fd.append('representationType', 'Original');

        return this.http.postProgress(endpoint, fd);
    }

    public createResumableUploadRecord(
        file: File,
        zoneId: ZoneId,
        recordType: ApiRecordType,
        parentRecordId: MediaObjectId | undefined,
        facialRecognitionConsent?: FacialRecognitionConsent
    ): Observable<ApiFragment> {
        const metadata: PartialMetadata = {};

        if (facialRecognitionConsent !== undefined) {
            metadata['Governance'] = {
                Consent: {
                    FacialRecognition: facialRecognitionConsent
                }
            };
        }

        const fd: FormData = this.getRecordCreationFormData(zoneId, recordType, metadata as Metadata, parentRecordId);

        fd.append('title', file.name);
        fd.append('fileUpload.fileSize', file.size.toString());
        fd.append('fileUpload.filename', file.name);

        return this.http.post<Metadata>(this.recordsEndpoint, fd).pipe(map(metadata => new ApiFragment(metadata)));
    }

    public createResumableUploadRepresentation(file: File, fragmentId: FragmentId): Observable<ApiFragment> {
        const endpoint: string = `${this.recordsEndpoint}/${fragmentId}/representations`;
        const fd: FormData = new FormData();

        fd.append('title', file.name);
        fd.append('fileUpload.fileSize', file.size.toString());
        fd.append('fileUpload.filename', file.name);
        fd.append('representationType', 'Original');

        return this.http.post<Metadata>(endpoint, fd).pipe(map(metadata => new ApiFragment(metadata)));
    }

    public createIntellectualRecord(
        zoneId: ZoneId,
        recordType: ApiRecordType,
        metadata: Metadata,
        parentRecordId?: MediaObjectId
    ): Observable<HttpEvent<unknown>> {
        const fd: FormData = this.getRecordCreationFormData(zoneId, recordType, metadata, parentRecordId);
        return this.http.postProgress(this.recordsEndpoint, fd);
    }

    private getRecordCreationFormData(
        zoneId: ZoneId,
        recordType: ApiRecordType,
        metadata?: Metadata,
        parentRecordId?: MediaObjectId
    ): FormData {
        let fd: FormData = new FormData();
        fd.append('zone', zoneId);
        fd.append('recordType', recordType.Name);
        if (parentRecordId) {
            fd.append('adoption.parentRecordId', parentRecordId);
            fd.append('adoption.profileStrategy', 'OVERWRITE');
        }

        if (metadata) {
            fd = this.appendMetadataAsJson(fd, new ApiFragment(metadata));
        }

        return fd;
    }

    public saveRecord(fragmentId: FragmentId, sidecars: PartialRecord[]): Observable<ApiFragment> {
        const request = `${this.recordsEndpoint}/${fragmentId}`;

        const sidecarSaveStreams$ = sidecars.map(sidecar => {
            const fd: FormData = this.appendMetadataAsJson(new FormData(), sidecar);
            const saveStream$: Observable<ApiFragment> = this.http
                .post<Metadata>(request, fd)
                .pipe(map(record => new ApiFragment(record)));

            return saveStream$;
        });

        return makeSequentialRequests(sidecarSaveStreams$).pipe(
            map(records => records[records.length - 1]),
            switchMap(apiResponse => WaitForRecordUpdate(fragmentId, this, apiResponse.getLastModifiedDate()))
        );
    }

    public createRecord(metadata: PartialRecord, options: CreateRecordOptions): Observable<ApiFragment> {
        const request = `${this.recordsEndpoint}`;

        let fd: FormData = new FormData();

        // validation
        if (metadata.getTitle()) {
            fd.append('title', metadata.getTitle()!);
        } else {
            return throwError('Title needs to be supplied');
        }

        fd.append('recordType', metadata.getRecordType());
        fd.append('type', 'metadataonly');

        if ('zoneId' in options && options.zoneId) {
            fd.append('zone', options.zoneId);
        } else if ('ingestSpaceId' in options && options.ingestSpaceId) {
            fd.append('ingestSpaceId', options.ingestSpaceId);
        } else {
            return throwError('Either zoneId or ingestSpaceId needs to be supplied');
        }

        if (options.parentRecordId) {
            fd.append('adoption.parentRecordId', options.parentRecordId);
            fd.append('adoption.profileStrategy', 'OVERWRITE');
        }

        fd = this.appendMetadataAsJson(fd, metadata);
        const createRecordStream$: Observable<Metadata> = this.http.post<Metadata>(request, fd);

        return createRecordStream$.pipe(
            take(1),
            catchError(error => {
                return throwError(error);
            }),
            // MED-885: Necessary in case post failed.
            // Would otherwise still continue to WaitForIndex and try to POST 20 more times
            // instead of polling for the fragment despite error thrown
            switchMap(result =>
                iif(
                    () => result instanceof Error,
                    throwError(result),
                    of(result).pipe(
                        map(resultMetadata => new ApiFragment(resultMetadata)),
                        switchMap(fragment => this.getRecord(fragment.getFragmentId())),
                        waitForIndex()
                    )
                )
            )
        );
    }

    public checkRecordValidity(record: ApiFragment): Observable<ValidationResponse> {
        const request = `${this.recordsEndpoint}/validations`;

        const body: { Sidecar: Metadata } = {
            Sidecar: record.metadata
        };

        return this.http
            .post<ValidationResponse>(request, body)
            .pipe(map(response => ({ ...response, Type: ValidationResponseType.MissingRequiredFields })));
    }

    public getChildIds(parentId: Identifier, statusList?: RecordStatus[]): Observable<RecordId[]> {
        let params: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() });

        // Retrieve both direct and further descendants
        params = params
            .append('level', 'All')
            .append('recordStructure', 'Data')
            .append('recordStructure', 'DataFlat')
            .append('recordStructure', 'Intellectual');

        if (statusList) {
            statusList.forEach(status => {
                params = params.append('recordStatus', status);
            });
        }

        const request = `${this.recordsEndpoint}/${parentId}/children?${params.toString()}`;

        return this.http.get<RecordId[]>(request);
    }

    // #region publish
    public publishRecord(record: ApiFragment): Observable<boolean> {
        const fragmentId: FragmentId = record.getFragmentId();
        const request = `${this.recordsEndpoint}/${fragmentId}`;

        return this.http.post<ApiFragment>(request, { Publish: true }).pipe(mapTo(true));
    }
    // #endregion

    // #region delete
    public deleteRecord(record: ApiFragment): Observable<boolean> {
        const fragmentId: FragmentId = record.getFragmentId();
        const request = `${this.recordsEndpoint}/${fragmentId}`;

        return this.http.delete<boolean>(request).pipe(WaitForRecordDelete(record, this), mapTo(true));
    }

    public permanentlyDeleteRecord(record: ApiFragment): Observable<boolean> {
        const recordId: FragmentId = record.getFragmentId();
        const request = `${this.recordsEndpoint}/${recordId}`;
        const deleteOptions: { body: { Permanent: boolean } } = { body: { Permanent: true } };

        return this.http
            .delete<boolean>(request, deleteOptions)
            .pipe(WaitForRecordDelete(record, this, true), mapTo(true));
    }

    public bulkPermanentlyDeleteRecords(records: readonly ApiFragment[]): Observable<boolean[]> {
        const requests$: Observable<boolean>[] = records.map(this.permanentlyDeleteRecord.bind(this));

        return makeSequentialRequests(requests$);
    }

    public recoverDeletedRecord(record: ApiFragment): Observable<ApiFragment> {
        const recordId: FragmentId = record.getFragmentId();
        const request = `${this.recordsEndpoint}/${recordId}`;
        const recoverOptions: { Undelete: boolean } = { Undelete: true };

        return this.http
            .post<Metadata>(request, recoverOptions)
            .pipe(
                switchMap(updatedRecord =>
                    WaitForRecordUpdate(recordId, this, new ApiFragment(updatedRecord).getLastModifiedDate())
                )
            );
    }

    public bulkRecoverDeletedRecords(records: readonly ApiFragment[]): Observable<ApiFragment[]> {
        const request$: Observable<ApiFragment>[] = records.map(this.recoverDeletedRecord.bind(this));
        return makeSequentialRequests(request$);
    }
    // #endregion

    public countRecords(fragmentIds: FragmentId[], restriction?: Restriction): Observable<number> {
        const queryParts: QueryPart[] = [];
        if (fragmentIds.length > 0) {
            queryParts.push(
                new QueryPart({
                    field: 'FragmentId',
                    // MatchAll will apply the changes to -every- record in the system, even with fragmentIds specified!
                    strategy: QueryPartStrategy.MatchAtLeastOne,
                    values: fragmentIds
                })
            );
        }
        const query: string = ApiQueryBuilder.queryToLuceneQuery(new ApiQuery(queryParts, restriction));
        const data: any = {
            Q: query,
            NrOfResults: 1,
            StartIndex: 0
        };

        return this.http
            .post(`${this.recordsEndpoint}/search`, data)
            .pipe(map((response: any) => response['TotalNrOfResults']));
    }
}
